import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Download, X, Trash2, Phone, MapPin, ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { adminWardIssues, adminUpdateWardIssue, adminDeleteWardIssue, resolvedImageUrl, API, TOKEN_KEY } from "@/lib/api";
import { ImageUpload } from "@/admin/FormFields";
import { ISSUE_CATEGORIES } from "@/data/constants";

const STATUSES = ["New", "Reviewed", "In Progress", "Escalated", "Resolved"];
const statusColor = {
  New: "bg-blue-100 text-blue-700",
  Reviewed: "bg-purple-100 text-purple-700",
  "In Progress": "bg-amber-100 text-amber-700",
  Escalated: "bg-red-100 text-red-700",
  Resolved: "bg-emerald-100 text-emerald-700",
};

export default function WardIssuesAdmin() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: "All", category: "All", ward: "" });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ward-issues", filters, page],
    queryFn: () => adminWardIssues({
      status: filters.status, category: filters.category, ward: filters.ward || undefined,
      page, pageSize: PAGE_SIZE,
    }),
    keepPreviousData: true,
  });
  const issues = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => adminUpdateWardIssue(id, data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["admin-ward-issues"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["resolved-issues"] });
      setSelected(updated);
      toast.success("Updated");
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id) => adminDeleteWardIssue(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ward-issues"] });
      setSelected(null);
      toast.success("Deleted");
    },
  });

  const exportCsv = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API}/admin/ward-issues-export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "ward_issues.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="ward-issues-admin">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">Hoodi Ward 29 Citizen Issues</h1>
          <p className="text-slate-500 text-sm mt-1">{total} issue(s)</p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white font-semibold text-sm hover:bg-navy-700">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value })}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm outline-none" data-testid="filter-status">
          <option>All</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.category} onChange={(e) => setFilter({ category: e.target.value })}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm outline-none" data-testid="filter-category">
          <option>All</option>{ISSUE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input placeholder="Filter by ward/locality" value={filters.ward}
          onChange={(e) => setFilter({ ward: e.target.value })}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm outline-none flex-1 min-w-[200px]" data-testid="filter-ward" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Ref", "Title", "Category", "Locality", "Phone", "Status", "Date"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 font-semibold text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {issues.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No issues found.</td></tr>}
                {issues.map((it) => (
                  <tr key={it.id} onClick={() => setSelected(it)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`issue-row-${it.id}`}>
                    <td className="px-5 py-3 font-mono text-xs text-gold font-bold">{it.referenceId || "—"}</td>
                    <td className="px-5 py-3 font-medium text-navy max-w-xs truncate">{it.title}</td>
                    <td className="px-5 py-3 text-slate-600">{it.category}</td>
                    <td className="px-5 py-3 text-slate-600">{it.locality}{it.ward ? ` / ${it.ward}` : ""}</td>
                    <td className="px-5 py-3 text-slate-600">{it.phone}</td>
                    <td className="px-5 py-3"><span className={`text-[11px] px-2.5 py-1 rounded-full ${statusColor[it.status] || "bg-slate-100"}`}>{it.status}</span></td>
                    <td className="px-5 py-3 text-slate-400 text-xs">{(it.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50" data-testid="issues-pagination">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold text-navy disabled:opacity-40 hover:bg-white">
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <span className="text-xs text-slate-500 font-mono">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold text-navy disabled:opacity-40 hover:bg-white">
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="issue-detail-drawer">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-display text-lg font-semibold text-navy">Issue Details</h2>
              <button onClick={() => setSelected(null)} className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gold/15 text-gold mb-2">{selected.category}</span>
                <h3 className="font-display text-xl font-semibold text-navy">{selected.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{selected.description}</p>
              </div>
              {selected.image && <img src={selected.image} alt="issue" className="rounded-xl w-full object-cover max-h-72" />}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Reference ID" value={selected.referenceId || "—"} />
                <Info label="Name" value={selected.name} />
                <Info label="Phone" value={selected.phone} />
                <Info label="Email" value={selected.email || "—"} />
                <Info label="Locality" value={selected.locality} />
                <Info label="Ward" value={selected.ward || "—"} />
                <Info label="Submitted" value={(selected.created_at || "").slice(0, 10)} />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">Status</label>
                <div className="flex flex-wrap gap-2" data-testid="status-buttons">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => updateMut.mutate({ id: selected.id, data: { status: s } })}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${selected.status === s ? statusColor[s] + " ring-2 ring-offset-1 ring-navy/20" : "bg-slate-100 text-slate-500"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">Internal Notes</label>
                <textarea defaultValue={selected.internalNotes} rows={3} data-testid="internal-notes"
                  onBlur={(e) => e.target.value !== selected.internalNotes && updateMut.mutate({ id: selected.id, data: { internalNotes: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-gold resize-none text-sm" />
                <p className="text-xs text-slate-400 mt-1">Notes save when you click away.</p>
              </div>

              {selected.status === "Resolved" && (
                <div className="space-y-4 p-4 rounded-xl border border-slate-200 bg-emerald-50/40" data-testid="resolved-card-admin">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">"After" photo (resolution proof)</label>
                    <ImageUpload value={selected.resolvedImage} testid="resolved-image-upload" onChange={(v) => updateMut.mutate({ id: selected.id, data: { resolvedImage: v } })} />
                    <p className="text-xs text-slate-500 mt-1">Used as the AFTER panel in the auto-generated shareable resolved card.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Resolution note (public)</label>
                    <textarea defaultValue={selected.resolutionNote || ""} rows={2} data-testid="resolution-note"
                      onBlur={(e) => e.target.value !== (selected.resolutionNote || "") && updateMut.mutate({ id: selected.id, data: { resolutionNote: e.target.value } })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-gold resize-none text-sm" />
                  </div>
                  <a href={resolvedImageUrl(selected.referenceId)} target="_blank" rel="noreferrer" data-testid="view-resolved-card"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold">
                    <ImageIcon className="h-4 w-4" /> View shareable resolved card
                  </a>
                </div>
              )}

              {selected.status === "Resolved" && (
                <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-emerald-50/50 cursor-pointer" data-testid="showcase-toggle">
                  <input type="checkbox" checked={!!selected.showcase}
                    onChange={(e) => updateMut.mutate({ id: selected.id, data: { showcase: e.target.checked } })}
                    className="mt-0.5 h-5 w-5 rounded text-civic" data-testid="showcase-checkbox" />
                  <span className="text-sm text-slate-700">
                    <span className="font-semibold text-navy">Show on public "Recently Resolved" wall</span><br />
                    <span className="text-xs text-slate-500">Appears on the homepage. Citizen name and phone are never shown.</span>
                  </span>
                </label>
              )}

              <div className="flex gap-3 pt-2">
                <a href={`tel:${selected.phone}`} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-civic text-white font-semibold text-sm"><Phone className="h-4 w-4" /> Call</a>
                <button onClick={() => deleteMut.mutate(selected.id)} className="px-4 py-3 rounded-xl bg-red-50 text-red-500 font-semibold text-sm inline-flex items-center gap-2" data-testid="delete-issue"><Trash2 className="h-4 w-4" /> Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <p className="text-[11px] text-slate-400 uppercase">{label}</p>
      <p className="text-navy font-medium truncate">{value}</p>
    </div>
  );
}
